import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaSearch, FaTimes } from 'react-icons/fa';
import PostCard from '../PostCard';
import { slugify, tagPath } from '../../utils/blog';
import { getBlogIndex } from '../../utils/content';
import './style.scss';

const PER_PAGE = 9;

// Blog index: search, topic chips, featured latest post, paginated grid.
const BlogPost = () => {
    const [blogs, setBlogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState('');
    const [page, setPage] = useState(1);
    const topRef = useRef(null);

    useEffect(() => {
        // Already newest-first.
        getBlogIndex()
            .then(setBlogs)
            .finally(() => setLoading(false));
    }, []);

    // Most-used topics first.
    const topics = useMemo(() => {
        const counts = new Map();
        blogs.forEach((b) => (b.tags || []).forEach((t) => {
            const key = slugify(t);
            const entry = counts.get(key) || { label: t, count: 0 };
            if (/[A-Z]/.test(t) && !/[A-Z]/.test(entry.label)) entry.label = t;
            entry.count += 1;
            counts.set(key, entry);
        }));
        return [...counts.values()].sort((a, b) => b.count - a.count).slice(0, 12);
    }, [blogs]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return blogs;
        return blogs.filter((b) =>
            [b.title, b.excerpt, ...(b.tags || [])].some((s) => (s || '').toLowerCase().includes(q))
        );
    }, [blogs, query]);

    const searching = query.trim().length > 0;
    const featured = !searching && page === 1 ? filtered[0] : null;
    const rest = featured ? filtered.slice(1) : filtered;
    const totalPages = Math.max(1, Math.ceil((searching ? filtered.length : Math.max(0, filtered.length - 1)) / PER_PAGE));
    const pageItems = rest.slice((page - 1) * PER_PAGE, page * PER_PAGE);

    const goToPage = (p) => {
        if (p < 1 || p > totalPages || p === page) return;
        setPage(p);
        if (topRef.current) topRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    return (
        <div className="blogIndex" ref={topRef}>
            <div className="blogIndex__toolbar">
                <label className="blogIndex__search">
                    <FaSearch aria-hidden="true" />
                    <span className="visuallyHidden">Search articles</span>
                    <input
                        type="search"
                        placeholder="Search articles"
                        value={query}
                        onChange={(e) => { setQuery(e.target.value); setPage(1); }}
                    />
                    {query && (
                        <button type="button" aria-label="Clear search" onClick={() => setQuery('')}>
                            <FaTimes />
                        </button>
                    )}
                </label>
                {topics.length > 0 && (
                    <nav className="blogIndex__topics" aria-label="Topics">
                        {topics.map((t) => (
                            <Link key={t.label} to={tagPath(t.label)}>{t.label}</Link>
                        ))}
                    </nav>
                )}
            </div>

            {loading ? (
                <div className="blogIndex__grid" aria-busy="true">
                    {[0, 1, 2].map((i) => <div key={i} className="blogIndex__skeleton" />)}
                </div>
            ) : filtered.length === 0 ? (
                <p className="blogIndex__empty">
                    {searching ? <>No articles match “{query}”.</> : 'No articles yet.'}
                </p>
            ) : (
                <>
                    {searching && (
                        <p className="blogIndex__count">{filtered.length} article{filtered.length === 1 ? '' : 's'} found</p>
                    )}
                    {featured && (
                        <div className="blogIndex__featured">
                            <PostCard post={featured} featured headingLevel="h2" />
                        </div>
                    )}
                    <div className="blogIndex__grid">
                        {pageItems.map((blog) => (
                            <PostCard key={blog.slug} post={blog} headingLevel={featured ? 'h3' : 'h2'} />
                        ))}
                    </div>

                    {totalPages > 1 && (
                        <nav className="blogPagination" aria-label="Blog pagination">
                            <button className="pageBtn pageNav" onClick={() => goToPage(page - 1)} disabled={page === 1}>
                                ← Prev
                            </button>
                            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                                <button
                                    key={p}
                                    className={`pageBtn${p === page ? ' active' : ''}`}
                                    onClick={() => goToPage(p)}
                                    aria-current={p === page ? 'page' : undefined}
                                >
                                    {p}
                                </button>
                            ))}
                            <button className="pageBtn pageNav" onClick={() => goToPage(page + 1)} disabled={page === totalPages}>
                                Next →
                            </button>
                        </nav>
                    )}
                </>
            )}
        </div>
    );
};

export default BlogPost;
